<template>
  <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns }">
    <span class="pr-2">
      {{
        t('Reviews.product.view.reviewDetailTitle', {
          authorName: surveyResponse.reviewerFirstName || t('Reviews.product.review.history.unknownAuthor'),
        })
      }}
    </span>
    <SurveyResponseStatusBadge class="flex" :survey-response="surveyResponse" />
    <ReviewInteractionStatus
      v-if="remindersCount && remindersCount > 0"
      subject="product"
      :type="ReviewInteractionType.REMINDER_CHECK"
      :tooltip-text="t('Reviews.product.review.reminderStatusHint', { count: remindersCount })"
      class="ml-2"
    />
    <ReviewInteractionStatus v-if="hasReply" subject="product" :type="ReviewInteractionType.REPLY_CHECK" class="ml-2" />
    <template #actions>
      <TooltipWrapper>
        <template #trigger>
          <button
            v-if="canAddReply"
            class="btn-primary-color-s"
            data-testid="reviews-send-reply"
            :disabled="canAddReplyDisabled"
            @click="$emit('send-reply')"
          >
            {{ t('Reviews.product.view.actions.sendReply') }}
          </button>
        </template>
        <template v-if="canAddReplyDisabled" #content>
          <div class="max-w-xs" v-html="t('Reviews.product.review.sendReplyDisabledHint')" />
        </template>
      </TooltipWrapper>
    </template>
  </ContentCardSubheader>
</template>

<script setup lang="ts">
import { TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ReviewInteractionStatus from '@/components/ReviewsCommon/ReviewInteractionStatus/ReviewInteractionStatus.vue'
import { ReviewInteractionType } from '@/components/ReviewsCommon/ReviewInteractionStatus/types'
import { SurveyResponseState } from '@/composables/useSurveyResponseState.ts'
import type { ProductData } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types.ts'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import SurveyResponseStatusBadge from './SurveyResponseStatusBadge.vue'
import type { SurveyResponse } from './types'

const props = defineProps<{
  product: Pick<ProductData, 'configFlags'>
  surveyResponse: SurveyResponse
}>()

defineEmits(['send-reply'])

const { t } = useI18n()
const hasReply = computed(() => !!props.surveyResponse.review?.reviewReply)
const remindersCount = computed(() => props.surveyResponse.reminders.length)
const canAddReply = computed(() => props.surveyResponse.aasmState === SurveyResponseState.Published && !hasReply.value)
const canAddReplyDisabled = computed(() => !props.product.configFlags.reviewRepliesAllowed)
</script>
